// Phase 18 · Community · Create post modal (10 post types)
import React, { useEffect, useState } from "react";
import { X, Sparkles, ShoppingBag, Video, Link2, Layers } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const POST_TYPES = [
    { value: "text", label: "Text", icon: <Sparkles size={14} /> },
    { value: "discussion", label: "Discussion", icon: <Sparkles size={14} /> },
    { value: "case_study", label: "Case Study", icon: <Layers size={14} /> },
    { value: "link", label: "Link", icon: <Link2 size={14} /> },
    { value: "video", label: "Video (YT/Vimeo/Loom)", icon: <Video size={14} /> },
    { value: "asset_marketplace", label: "Marketplace asset", icon: <ShoppingBag size={14} /> },
    { value: "mini_landing", label: "Mini-landing", icon: <Layers size={14} /> },
];

export default function CreatePostModal({ onClose, onCreated, initialCategory }) {
    const [type, setType] = useState("text");
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [category, setCategory] = useState(initialCategory || "marketing");
    const [embedUrl, setEmbedUrl] = useState("");
    const [assetRef, setAssetRef] = useState("");
    const [landingRef, setLandingRef] = useState("");
    const [categories, setCategories] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${API}/api/community/categories`, { credentials: "include" });
                if (r.ok) {
                    const d = await r.json();
                    setCategories(d.items || []);
                }
            } catch {}
        })();
    }, []);

    const submit = async () => {
        if (!title.trim() || title.trim().length < 3) {
            toast.error("Title needs at least 3 chars");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                post_type: type,
                title: title.trim(),
                body: body.trim(),
                category_slug: category,
            };
            if (["video", "embed", "link"].includes(type)) payload.embed_url = embedUrl.trim();
            if (type === "asset_marketplace") payload.marketplace_asset_ref = assetRef.trim();
            if (type === "mini_landing") payload.mini_landing_ref = landingRef.trim();

            const r = await fetch(`${API}/api/community/posts`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(payload),
            });
            if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                toast.error(err.detail?.error || "Post creation failed");
                return;
            }
            const created = await r.json();
            toast.success("Post published");
            onCreated?.(created.id);
        } catch { toast.error("Network error"); }
        finally { setBusy(false); }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="create-post-modal"
        >
            <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl w-full max-w-xl p-6 relative max-h-[90vh] overflow-y-auto">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-4 right-4 text-neutral-500 hover:text-neutral-200"
                    data-testid="create-post-close"
                >
                    <X size={18} />
                </button>
                <h2 className="text-lg font-semibold text-neutral-100 mb-4">Create new post</h2>

                <label className="block text-xs text-neutral-400 mb-1">Post type</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mb-4">
                    {POST_TYPES.map((t) => (
                        <button
                            key={t.value}
                            type="button"
                            onClick={() => setType(t.value)}
                            className={`px-2 py-1.5 rounded-lg border text-xs flex items-center justify-center gap-1 transition-all ${type === t.value ? "border-[#FFFFFF] bg-[#FFFFFF]/10 text-[#FFFFFF]" : "border-[#1A1A1A] text-neutral-400 hover:border-[#FFFFFF]/30"}`}
                            data-testid={`post-type-${t.value}`}
                        >
                            {t.icon} {t.label}
                        </button>
                    ))}
                </div>

                <label className="block text-xs text-neutral-400 mb-1">Category</label>
                <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF] mb-4"
                    data-testid="post-category"
                >
                    {categories.map((c) => (
                        <option key={c.slug} value={c.slug}>{c.name}</option>
                    ))}
                </select>

                <label className="block text-xs text-neutral-400 mb-1">Title</label>
                <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Post title"
                    maxLength={200}
                    className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF] mb-4"
                    data-testid="post-title-input"
                />

                {(type === "video" || type === "embed" || type === "link") && (
                    <>
                        <label className="block text-xs text-neutral-400 mb-1">
                            {type === "video" ? "Video URL (YouTube / Vimeo / Loom)" : "URL"}
                        </label>
                        <input
                            type="url"
                            value={embedUrl}
                            onChange={(e) => setEmbedUrl(e.target.value)}
                            placeholder="https://…"
                            className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF] mb-4"
                            data-testid="post-embed-url"
                        />
                    </>
                )}
                {type === "asset_marketplace" && (
                    <>
                        <label className="block text-xs text-neutral-400 mb-1">Marketplace asset ID</label>
                        <input
                            type="text"
                            value={assetRef}
                            onChange={(e) => setAssetRef(e.target.value)}
                            placeholder="Paste the marketplace asset id"
                            className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF] mb-4 font-mono"
                            data-testid="post-asset-ref"
                        />
                    </>
                )}
                {type === "mini_landing" && (
                    <>
                        <label className="block text-xs text-neutral-400 mb-1">Mini-landing ID</label>
                        <input
                            type="text"
                            value={landingRef}
                            onChange={(e) => setLandingRef(e.target.value)}
                            placeholder="Paste the mini-landing id"
                            className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF] mb-4 font-mono"
                            data-testid="post-landing-ref"
                        />
                    </>
                )}

                <label className="block text-xs text-neutral-400 mb-1">Body {type !== "link" && "(optional)"}</label>
                <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={5}
                    maxLength={20000}
                    placeholder="Your message…"
                    className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                    data-testid="post-body-input"
                />

                <div className="flex justify-end gap-2 mt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm text-neutral-400 hover:text-neutral-200"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy || !title.trim()}
                        className="px-4 py-2 text-sm bg-white text-neutral-900 font-semibold rounded disabled:opacity-40"
                        data-testid="post-submit"
                    >
                        {busy ? "..." : "Publish"}
                    </button>
                </div>
            </div>
        </div>
    );
}
